/**
 * Утилита для работы с DOM-элементами.
 */
const DOM = {
  /**
   * Получает один или несколько элементов по селектору.
   *
   * @param {string} selector - CSS-селектор для поиска элементов.
   * @returns {Element|NodeList|null} Возвращает один элемент, список элементов или null, если ничего не найдено.
   */
  select(selector, isMultiple = false) {
    const elements = document.querySelectorAll(selector);
    if (elements.length === 1 && !isMultiple) {
      return elements[0];
    } else if (elements.length === 0) {
      return null;
    }
    return elements;
  },

  /**
   * Добавляет один или несколько классов к одному или нескольким элементам.
   *
   * @param {Element|NodeList|HTMLCollection|Array} elements - Один элемент или коллекция DOM-элементов.
   * @param {...string} classNames - Один или несколько классов, которые нужно добавить.
   */
  addClass(elements, ...classNames) {
    if (!elements) return;

    if (elements instanceof Element) {
      // Если передан один элемент
      elements.classList.add(...classNames);
    } else {
      // Если передана коллекция элементов
      [...elements].forEach(el => el.classList.add(...classNames));
    }
  },


  /**
 * Удаляет один или несколько классов у одного или нескольких элементов.
  *
  * @param {Element|NodeList|HTMLCollection|Array} elements - Один элемент или коллекция DOM-элементов.
  * @param {...string} classNames - Один или несколько классов, которые нужно удалить.
  */
  removeClass(elements, ...classNames) {
    if (!elements) return;

    if (elements instanceof Element) {
      // Если передан один элемент
      elements.classList.remove(...classNames);
    } else {
      // Если передана коллекция элементов
      [...elements].forEach(el => el.classList.remove(...classNames));
    }
  },
  
  /**
   * Переключает класс элемента.
   *
   * @param {Element} el - DOM-элемент.
   * @param {string} className - Название класса.
   */
  toggleClass(el, className) {
    if (el) el.classList.toggle(className);
  },

  /**
   * Проверяет наличие класса у элемента.
   *
   * @param {Element} el - DOM-элемент.
   * @param {string} className - Название класса.
   * @returns {boolean} Возвращает true, если класс присутствует, иначе false.
   */
  hasClass(el, className) {
    return el ? el.classList.contains(className) : false;
  },

  /**
   * Устанавливает текстовое содержимое элемента.
   *
   * @param {Element} el - DOM-элемент.
   * @param {string} text - Текстовое содержимое.
   */
  setText(el, text) {
    if (el) el.textContent = text;
  },

  /**
   * Устанавливает HTML-содержимое элемента.
   *
   * @param {Element} el - DOM-элемент.
   * @param {string} html - HTML-код.
   */
  setHTML(el, html) {
    if (el) el.innerHTML = html;
  },

  /**
   * Очищает содержимое элемента.
   *
   * @param {Element} el - DOM-элемент.
   */
  clear(el) {
    if (el) el.innerHTML = '';
  },

  /**
   * Создает новый элемент с указанным классом.
   *
   * @param {string} tag - Тег элемента.
   * @param {string} [className=''] - Класс элемента (необязательный параметр).
   * @returns {Element} Созданный элемент.
   */
  createElement(tag, className = '') {
    const el = document.createElement(tag);
    if (className) el.classList.add(className);
    return el;
  },

  /**
   * Находит первый дочерний элемент по селектору.
   *
   * @param {Element} el - Родительский элемент.
   * @param {string} selector - CSS-селектор.
   * @returns {Element|null} Найденный элемент или null.
   */
  find(el, selector) {
    return el.querySelector(selector);
  },

  /**
   * Находит все дочерние элементы по селектору.
   *
   * @param {Element} el - Родительский элемент.
   * @param {string} selector - CSS-селектор.
   * @returns {NodeList} Список найденных элементов.
   */
  findAll(el, selector) {
    return el.querySelectorAll(selector);
  },

  /**
   * Возвращает дочерний элемент по индексу.
   *
   * @param {Element} el - Родительский элемент.
   * @param {number} index - Индекс дочернего элемента.
   * @returns {Element|null} Дочерний элемент или null.
   */
  children(el, index) {
    return el.children[index] || null;
  },

  /**
   * Возвращает родительский элемент.
   *
   * @param {Element} el - Дочерний элемент.
   * @returns {Element|null} Родительский элемент или null.
   */
  parent(el) {
    return el ? el.parentElement : null;
  },

  /**
   * Возвращает предыдущий соседний элемент.
   *
   * @param {Element} el - Текущий элемент.
   * @returns {Element|null} Предыдущий соседний элемент или null.
   */
  prev(el) {
    return el ? el.previousElementSibling : null;
  },

  /**
   * Возвращает следующий соседний элемент.
   *
   * @param {Element} el - Текущий элемент.
   * @returns {Element|null} Следующий соседний элемент или null.
   */
  next(el) {
    return el ? el.nextElementSibling : null;
  },

  /**
   * Возвращает всех соседей элемента.
   *
   * @param {Element} el - Текущий элемент.
   * @returns {HTMLCollection} Список соседних элементов.
   */
  siblings(el) {
    return el && el.parentElement ? el.parentElement.children : [];
  },
};

window.DOM = DOM;